import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// i18n import
import "./i18n";
import "flag-icons/css/flag-icons.min.css";

// Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css";
// Bootstrap Bundle JS
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

//페이지 연결
import Dashboard from "./pages/dashboard/Dashboard";
import Developer from "./pages/developer/Developer";
import Login from "./pages/login/Login";
import NotFound from "./pages/notFound/NotFound";
import DevSchools from "./pages/developer/components/DevSchools";
import BasicInfo from "./pages/basicInfo/BasicInfo";
import NotLogin from "./pages/developer/components/NotLogin";
import AuthorityMaking from "./pages/developer/subpages/AuthorityMaking";
import TeacherInfo from "./pages/teacherinfo/TeacherInfo";
import MyInfo from "./pages/myInfo/MyInfo";
import OrganizationSetting from "./pages/organizationSetting/OrganizationSetting";
import RegistrationInfo from "./pages/registrationInfo/RegistrationInfo";
import Attendance from "./pages/attendance/Attendance";
import StudentInfo from "./pages/studentInfo/StudentInfo";
import Counseling from "./pages/counseling/Counseling";
import CreateActivities from "./pages/createActivities/CreateActivities";
import TagSetting from "./pages/tagSetting/TagSetting";
import DoActivities from "./pages/doActivities/DoActivities";
import PeriodSetting from "./pages/periodSetting/PeriodSetting";
import AttendanceStatistics from "./pages/dataAndStatistics/attendanceStatistics/AttendanceStatistics";
import LessonSetting from "./pages/lessonRelated/lessonSetting/LessonSetting";
import CreateLessons from "./pages/lessonRelated/createLessons/CreateLessons";
import DoLessons from "./pages/lessonRelated/doLessons/DoLessons";
import VariousRecords from "./pages/variousRecords/VariousRecords";
import SchoolLifeRecords from "./pages/schoolLifeRecords/SchoolLifeRecords";
import ReportCard from "./pages/printingRelated/reportCard/ReportCard";
import SchoolLifeRecordForm from "./pages/printingRelated/schoolLifeRecordForm/SchoolLifeRecordForm";
import OfficialDocuments from "./pages/printingRelated/officialDocuments/OfficialDocuments";
import FormsAndTemplates from "./pages/printingRelated/formsAndTemplates/FormsAndTemplates";
import SelfReflection from "./pages/studentPages/selfReflection/SelfReflection";
import StudentSelfReflection from "./pages/teacherPages/studentSelfReflection/StudentSelfReflection";
import Groups from "./pages/adminRelated/groups/Groups";
import GroupsAuthorities from "./pages/adminRelated/groupsAuthorities/GroupsAuthorities";
import DataMigration from "./pages/developer/subpages/DataMigration/DataMigration";
import CustomizedAttendance from "./pages/dataAndStatistics/customizedAttendance/CustomizedAttendance";
import StuReportCard from "./pages/studentPages/stuPrintingRelated/stuReportCard/StuReportCard";
import LinkStudent from "./pages/parentRelated/linkStudent/LinkStudent";
import PaStuReportCard from "./pages/parentRelated/paStuReportCard/PaStuReportCard";
import EmailVerification from "./pages/commonPages/emailVerification/EmailVerification";
import ResetPassword from "./pages/commonPages/resetPassword/ResetPassword";
import JubileeAward from "./pages/customizedPages/jubileeAward/JubileeAward";
import StudentRecords from "./pages/studentPages/stuPrintingRelated/studentRecords/StudentRecords";
import Transcript from "./pages/printingRelated/transcript/Transcript";
import GradeDataTable from "./pages/dataAndStatistics/gradeDataTable/GradeDataTable";
import SurveySetting from "./pages/adminRelated/surveySetting/SurveySetting";
import SurveyStatistics from "./pages/dataAndStatistics/surveyStatistics/SurveyStatistics";
import SubjectSurvey from "./pages/studentPages/studentSurvey/SubjectSurvey";
import SurveyPreview from "./pages/adminRelated/surveySetting/components/SurveyPreview";
import StudentRecordsView from "./pages/dataAndStatistics/studentRecordsView/StudentRecordsView";
import AttendanceStatisticsByDate from "./pages/dataAndStatistics/attendanceStatisticsByDates/AttendanceStatisticsByDate";
import GemeralManagement from "./pages/adminRelated/generalManagement/GemeralManagement";
import PaOfficialDocuments from "./pages/parentRelated/officialDocuments/PaOfficialDocuments";
import StuOfficialDocuments from "./pages/studentPages/officialDocuments/StuOfficialDocuments";
import ExtraLessonManagement from "./pages/adminRelated/extraLessonManagement/ExtraLessonManagement";
import ManageSchools from "./pages/developer/subpages/ManageSchools/ManageSchools";
import ExtraSettings from "./pages/adminRelated/extraSettings/ExtraSettings";

// src/index.js 상단에 추가
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://a6cce6229646b2c2f8ec4ec83fa5f631@o4510628612145152.ingest.us.sentry.io/4510628613652480",
  sendDefaultPii: true,
  enableLogs: true,
  // enabled: process.env.NODE_ENV === "production",
});

const queryClient = new QueryClient();
const router = createBrowserRouter([
  {
    path: "/developer",
    element: <Developer />,
    errorElement: <NotFound />,
    children: [
      { path: "/developer", element: <NotLogin /> },
      { path: "/developer/devschools", element: <DevSchools /> },
      { path: "/developer/school-management", element: <ManageSchools /> },
      { path: "/developer/authoritymaking", element: <AuthorityMaking /> },
      { path: "/developer/data-migration", element: <DataMigration /> },
    ],
  },
  {
    path: "/",
    element: <App />,
    errorElement: <NotFound />,
    children: [
      { path: "/login", element: <Login /> },
      { path: "/:schoolid/:userrole/dashboard", element: <Dashboard /> },
      { path: "/:schoolid/:userrole/myinfo", element: <MyInfo /> },
      { path: "/email-verification", element: <EmailVerification /> },
      { path: "/reset-password", element: <ResetPassword /> },

      //---------------------- 관리자 페이지 ----------------------------

      { path: "/:schoolid/teacher/teacherinfo", element: <TeacherInfo /> },
      {
        path: "/:schoolid/teacher/organizationsetting",
        element: <OrganizationSetting />,
      },
      {
        path: "/:schoolid/teacher/tagsetting",
        element: <TagSetting />,
      },
      {
        path: "/:schoolid/teacher/periodsetting",
        element: <PeriodSetting />,
      },
      {
        path: "/:schoolid/teacher/lessonsetting",
        element: <LessonSetting />,
      },
      {
        path: "/:schoolid/teacher/groups",
        element: <Groups />,
      },
      {
        path: "/:schoolid/teacher/groups-authorities",
        element: <GroupsAuthorities />,
      },
      {
        path: "/:schoolid/teacher/survey-setting",
        element: <SurveySetting />,
      },
      {
        path: "/:schoolid/teacher/general-mgt",
        element: <GemeralManagement />,
      },
      {
        path: "/:schoolid/teacher/extra-lesson-mgt",
        element: <ExtraLessonManagement />,
      },
      {
        path: "/:schoolid/teacher/extra-settings",
        element: <ExtraSettings />,
      },

      //---------------------- 교직원 페이지 ----------------------------

      { path: "/:schoolid/teacher/basicinfo", element: <BasicInfo /> },
      {
        path: "/:schoolid/teacher/registrationinfo",
        element: <RegistrationInfo />,
      },

      {
        path: "/:schoolid/teacher/attendance",
        element: <Attendance />,
      },
      {
        path: "/:schoolid/teacher/studentinfo",
        element: <StudentInfo />,
      },
      {
        path: "/:schoolid/teacher/counseling",
        element: <Counseling />,
      },
      {
        path: "/:schoolid/teacher/createactivities",
        element: <CreateActivities />,
      },
      {
        path: "/:schoolid/teacher/doactivities",
        element: <DoActivities />,
      },
      {
        path: "/:schoolid/teacher/attendancestatistics",
        element: <AttendanceStatistics />,
      },
      {
        path: "/:schoolid/teacher/createlessons",
        element: <CreateLessons />,
      },
      {
        path: "/:schoolid/teacher/doLessons",
        element: <DoLessons />,
      },
      {
        path: "/:schoolid/teacher/variousrecords",
        element: <VariousRecords />,
      },
      {
        path: "/:schoolid/teacher/schoolliferecords",
        element: <SchoolLifeRecords />,
      },
      {
        path: "/:schoolid/teacher/reportcard",
        element: <ReportCard />,
      },
      {
        path: "/:schoolid/teacher/schoolliferecord-form",
        element: <SchoolLifeRecordForm />,
      },
      {
        path: "/:schoolid/teacher/official-documents",
        element: <OfficialDocuments />,
      },
      {
        path: "/:schoolid/teacher/forms-and-templates",
        element: <FormsAndTemplates />,
      },
      {
        path: "/:schoolid/teacher/student-self-reflection",
        element: <StudentSelfReflection />,
      },
      {
        path: "/:schoolid/teacher/grade-data-table",
        element: <GradeDataTable />,
      },
      {
        path: "/:schoolid/teacher/survey-statistics",
        element: <SurveyStatistics />,
      },
      { path: "/:schoolid/teacher/survey-preview", element: <SurveyPreview /> },

      {
        path: "/:schoolid/teacher/student-records-view",
        element: <StudentRecordsView />,
      },

      {
        path: "/:schoolid/teacher/attendance-by-date",
        element: <AttendanceStatisticsByDate />,
      },

      //---------------------- 교직원 중 커스텀 페이지 ----------------------------
      {
        path: "/:schoolid/teacher/customized-attendance",
        element: <CustomizedAttendance />,
      },
      {
        path: "/:schoolid/teacher/jubilee-award",
        element: <JubileeAward />,
      },

      {
        path: "/:schoolid/teacher/transcript",
        element: <Transcript />,
      },

      //---------------------- 학생 페이지 ----------------------------
      {
        path: "/:schoolid/student/self-reflection",
        element: <SelfReflection />,
      },
      {
        path: "/:schoolid/student/stu-reportcard",
        element: <StuReportCard />,
      },
      {
        path: "/:schoolid/student/student-records",
        element: <StudentRecords />,
      },
      {
        path: "/:schoolid/student/subject-survey",
        element: <SubjectSurvey />,
      },
      {
        path: "/:schoolid/student/student-official-documents",
        element: <StuOfficialDocuments />,
      },
      //---------------------- 학부모 페이지 ----------------------------
      {
        path: "/:schoolid/parent/link-student",
        element: <LinkStudent />,
      },
      {
        path: "/:schoolid/parent/stu-reportcard",
        element: <PaStuReportCard />,
      },
      {
        path: "/:schoolid/parent/parent-official-documents",
        element: <PaOfficialDocuments />,
      },
    ],
  },
]);

const handleWheel = (event) => {
  if (document.activeElement.type === "number") {
    event.preventDefault();
  }
};

window.addEventListener("wheel", handleWheel, { passive: false });

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Suspense fallback={<div>Loading...</div>}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {process.env.REACT_APP_BASE_URL === "http://localhost:8080/" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </Suspense>
  </React.StrictMode>
);

// 서비스 워커 등록
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
      registration.waiting.addEventListener("statechange", (event) => {
        if (event.target.state === "activated") {
          if (window.confirm("새로운 버전이 있습니다. 새로고침하시겠습니까?")) {
            window.location.reload();
          }
        }
      });
    }
  },
});

// Cleanup event listener on unmount
window.addEventListener("beforeunload", () => {
  window.removeEventListener("wheel", handleWheel);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
